import { useRef, useState } from 'react';
import { durationToPercentage } from '../decodo/components/atoms/Audio/utils';

/**
 * @returns Everything you need to control media playback
 */
export function useMediaPlayback<T extends HTMLMediaElement>() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [duration, setMediaLength] = useState<number>(0);

  /**
   * Ref to the audio or video element.
   */
  const mediaRef = useRef<T>(null);
  /**
   * Ref to the timeline input [type='range'] element, if one is needed.
   */
  const timelineRef = useRef<HTMLInputElement>(null);

  function handlePlaybackComplete() {
    setIsPlaying(false);
    setProgress(0);
  }

  function togglePlay() {
    if (!mediaRef.current) return;

    if (mediaRef.current.paused) {
      mediaRef.current.play();
      setIsPlaying(true);
    } else {
      mediaRef.current?.pause();
      setIsPlaying(false);
    }
  }

  const handleMount = () => {
    const mediaElement = mediaRef.current;

    if (!mediaElement) return;

    setMediaLength(mediaElement.duration);
    handleChangeHeadPosition();
  };

  function handleUpdateTime() {
    if (!mediaRef.current) return;
    if (!timelineRef.current) return;

    const percentagePosition = durationToPercentage(mediaRef.current || {});

    timelineRef.current.style.backgroundSize = `${percentagePosition}% 100%`;
    timelineRef.current.value = String(percentagePosition);

    setProgress(mediaRef.current.currentTime);
  }

  function handleChangeHeadPosition() {
    if (!mediaRef.current) return;
    if (!timelineRef.current) return;

    const media = mediaRef.current;
    const timeline = timelineRef.current;

    const time = (Number(timeline.value) * Number(media.duration)) / 100;
    media.currentTime = time;

    handleUpdateTime();
  }

  return {
    progress,
    /**
     * full video duration in seconds
     */
    duration,
    mediaRef,
    timelineRef,
    isPlaying,
    handlePlaybackComplete,
    togglePlay,
    handleMount,
    handleUpdateTime,
    handleChangeHeadPosition,

    /**
     *  internal methods, try to avoid these
     * */
    _setMediaLength: setMediaLength,
    _setProgress: setProgress,
    _setIsPlaying: setIsPlaying,
  };
}
